import { FC, useState } from 'react'
import { logger } from 'helpers/logger'
import { useSwitchState } from 'hooks/useSwitchState'
import { ChangeImgButton } from './ChangeImgButton'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  Form,
  Input,
  Button,
  FormControl,
  ButtonLoader,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  UploadFileProps,
} from '@wellesley/ui'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { usePostApiV1UsersMutation } from '@wellesley/openapi'
import zod from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLastUser } from './useLastUser'
import { InputLimits } from 'components/form/InputLimits'
import { getDisplayUsername, UserAvatar } from '@wellesley/user'
import CommonDialog from 'components/modals/CommonDialog'
import {
  usernameMaxLength,
  usernameValidator,
  displayNameValidator,
  getUsernameConflictError,
} from '@wellesley/forms'

const limits = {
  displayName: 56,
  username: usernameMaxLength,
}

const formSchema = zod.object({
  username: usernameValidator({ showInlineError: false }),
  displayName: displayNameValidator,
})

export type FormType = zod.TypeOf<typeof formSchema>

type NewAppProfileModalProps = {
  appId: string
  isOpen: boolean
  onAdd?: (username: string) => void
  onCancel?: () => void
}
export const NewAppProfileModal: FC<NewAppProfileModalProps> = ({
  isOpen,
  onAdd,
  onCancel,
}) => {
  const [isLoading, startLoading, stopLoading] = useSwitchState()
  const [addAppUser] = usePostApiV1UsersMutation()
  const { avatar, displayName, username } = useLastUser()
  const [avatarLocal, setAvatarLocal] = useState<UploadFileProps>()
  // const [uploadMedia] = useUploadMedia(avatarLocal ? [avatarLocal] : [])
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: displayName,
      username: username,
    },
  })

  const onSubmit: SubmitHandler<FormType> = async ({
    username,
    displayName,
  }) => {
    try {
      startLoading()
      // const uploadIds = await uploadMedia()
      // console.log('avatarId = ', uploadIds?.[0]) //TODO wait for avatarId in addAppUser
      await addAppUser({
        userCreate: {
          displayName,
          username,
          actorType: 'Service',
          avatarId: null,
          headerId: null,
        },
      })
      // await updateUser({ userId, userUpdate })
      onAdd?.(username)
    } catch (e) {
      logger.error('NewAppProfileModal submit error: ', e)
      const error = e as FetchBaseQueryError

      const { isError: isConflictError, message } =
        getUsernameConflictError(error)

      if (isConflictError) {
        form.setError('username', {
          message,
        })
      }
    } finally {
      stopLoading()
    }
  }

  const isDisabled =
    form.getFieldState('displayName').invalid ||
    form.getFieldState('username').invalid ||
    !form.getValues().displayName ||
    !form.getValues().username

  return (
    <CommonDialog open={isOpen} onCloseModal={onCancel} title="Add an app">
      <p className="mt-4 text-sm font-medium text-gray-500">
        Create a new user name to join the APP NAME and with more text like so
        it is gonna work too
      </p>
      <div className="my-4 flex items-center justify-between">
        <div className="flex items-center">
          <UserAvatar
            size={96}
            name={form.getValues().displayName || ''}
            imgUrl={avatar || avatarLocal?.objectUrl}
          />
          <div className="ml-4 text-gray-700">
            <h4 className="mb-1 text-base font-semibold leading-[22px]">
              {form.getValues().displayName}
            </h4>
            {form.getValues().username && (
              <span className="text-base font-semibold leading-[14px]">
                {getDisplayUsername(form.getValues().username)}
              </span>
            )}
          </div>
        </div>
        <ChangeImgButton
          onChange={(upload) => setAvatarLocal(upload)}
          className="mr-2 cursor-pointer uppercase"
        >
          Change Photo
        </ChangeImgButton>
      </div>
      <Form {...form}>
        <form
          className="even:*:my-5"
          onSubmit={(...args) => void form.handleSubmit(onSubmit)(...args)}
        >
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Display Name</FormLabel>
                  <InputLimits value={field.value} limit={limits.displayName} />
                </div>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem className="relative">
                <div className="flex items-center justify-between">
                  <FormLabel>User name</FormLabel>
                  <InputLimits value={field.value} limit={limits.username} />
                </div>
                <div className="absolute left-3 top-[34px] text-sm text-gray-400">
                  @
                </div>
                <FormControl>
                  <Input className="pl-7" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-full flex-row justify-end">
            <Button
              variant="tertiary"
              className="mr-3 uppercase"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <ButtonLoader
              size="m"
              variant="primary"
              type="submit"
              className="uppercase"
              disabled={isDisabled}
              isLoading={isLoading}
            >
              Add
            </ButtonLoader>
          </div>
        </form>
      </Form>
    </CommonDialog>
  )
}
